import { useState } from "react";

import { LinkedContact } from "@/Common/Api/Patient/Dto/LinkedContact";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { Modal } from "@/Common/Components/Modals/Modal";
import { IndividualAdvancedSearchControl } from "@/Common/Components/Search/IndividualAdvancedSearchControl";
import { IndividualAdvancedSearchResult } from "@/Common/Components/Search/IndividualAdvancedSearchResult";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { Person } from "@/Common/Models/Person/Person";
import { RelativeApiClient } from "@/Relative/Api/RelativeApiClient";
import { CreateRelative } from "@/Relative/Components/CreateRelative";
import { Relative } from "@/Relative/Models/Relative";

import styles from "./AddRelationPopup.module.scss";

interface IAddRelationPopupProps {
    /**
     * List of existing relations contactIds.
     */
    existingRelationsContactIds: string[];
    /**
     * On close or cancel event.
     */
    onClose: () => void;

    /**
     * On save event.
     */
    onSave: (relation: LinkedContact) => void;
}

function AddRelationPopup(props: IAddRelationPopupProps): JSX.Element {
    const [isCreationSectionOpen, setIsCreationSectionOpen] = useState<boolean>(false);
    const [isCreationFormValid, setIsCreationFormValid] = useState<boolean>(false);
    const [isOnSaveCalled, setIsOnSaveCalled] = useState<boolean>(false);
    const [newRelative, setNewRelative] = useState<Relative>(null);
    const [selectedPerson, setSelectedPerson] = useState<Person>(null);
    const [searchString, setSearchString] = useState<string>("");
    const [isRepresentative, setIsRepresentative] = useState<boolean>(false);
    const [representativeType, setRepresentativeType] = useState<string>(null);
    const [isEmergencyContact, setIsEmergencyContact] = useState<boolean>(false);
    const [relationshipTypeLookupString, setRelationshipTypeLookupString] = useState<string>("RLMother");

    const { getText } = useTranslation();

    const representativeTypes = cacheStore.GetRAMQTypeRepresentant(true);

    const IsSaveButtonDisabled = (): boolean => isCreationSectionOpen ? ((newRelative && !isCreationFormValid) || isOnSaveCalled) : !selectedPerson;

    const onSelectPerson = (person: Person, searchString: string): void => {
        setSelectedPerson(person);
        setSearchString(searchString);
    };

    const onSave = async (): Promise<void> => {
        let selected = selectedPerson;
        setIsOnSaveCalled(true);

        if (isCreationSectionOpen && newRelative) {
            selected = await RelativeApiClient.createRelativeAsync(newRelative);
            onSelectPerson(selected, "");
        }

        const newRelationship = new LinkedContact();
        newRelationship.TargetContactId = selected.ContactId;
        newRelationship.Individual = Person.ToIndividual(selected);
        newRelationship.ContactPoints = selected.ContactPoints;
        newRelationship.Addresses = selected.Addresses;
        newRelationship.PhoneNumbers = selected.Phones;
        newRelationship.Emails = selected.Emails;
        newRelationship.IsRepresentative = isRepresentative;
        newRelationship.RepresentativeType = representativeType;
        newRelationship.IsEmergencyContact = isEmergencyContact;
        const relLookupCode = relationshipTypeLookupString;
        newRelationship.RelationLookupId = cacheStore.FindLookupByLookupCode(relLookupCode, "RELATIONLINKS").LookupID;

        props.onSave?.(newRelationship);
    };

    const onCancel = (): void => {
        props.onClose?.();
    };

    const onDuplicatedSelect = (person: Person): void => {
        setIsCreationSectionOpen(false);
        selectPerson(person, "");
    };

    const selectPerson = (person: Person, searchString: string): void => {
        setSelectedPerson(person);
        setSearchString(searchString);
    };

    const clearPersonSelection = (): void => {
        setSelectedPerson(null);
    };

    const footer: JSX.Element = (
        <div className="p-footer">
            <div className="text-right">
                <ButtonControl
                    className="btn btn-default push-right-5"
                    onClick={() => onCancel()}
                    label={getText("CancelLabel")}
                />
                <ButtonControl
                    className="btn btn-primary"
                    label={getText("SaveLabel")}
                    onClick={() => onSave()}
                    disabled={IsSaveButtonDisabled()}
                    dataCy="addRelation-popup-save"
                />
            </div>
        </div>
    );

    return (
        <Modal
            title={getText("Relations_AddRelationLabelTitle")}
            width={600}
            maxHeight="calc(100vh - 200px)"
            top={50}
            onClose={() => onCancel()}
            footer={footer}
            showOverlay
            showX
            disableOverlayClose
        >
            <div className={styles.addRelationPopup} data-cy="addRelation-popup">
                <div className={styles.specificRelationTypesSection}>
                    <CheckboxControl
                        checked={isRepresentative}
                        label={getText("RepresentativeLabel")}
                        onChange={checked => {
                            setIsRepresentative(checked);
                            if (checked && !representativeType)
                                setRepresentativeType(representativeTypes?.firstOrDefault()[0]);
                        }}
                        dataCy="addRelation-popup-representative"
                    />
                    <CheckboxControl
                        checked={isEmergencyContact}
                        label={getText("EmergencyContactLabel")}
                        onChange={checked => setIsEmergencyContact(checked)}
                        dataCy="addRelation-popup-emergencyContact"
                    />
                </div>

                <div className={styles.relationshipSection}>
                    <div>
                        <label className="bold">{getText("RelationshipLabel")}</label>
                        <div>
                            <select
                                onChange={e => setRelationshipTypeLookupString(e.target.value)}
                                className="form-control myle-control"
                                value={relationshipTypeLookupString}
                                data-cy="addRelation-popup-relationship"
                            >
                                {cacheStore.GetRelationshipLookupsTuples(sessionStore.selectedFile.Gender()).map((p, idx) => (
                                    <option
                                        key={`${p[0].LookupID}_${idx}`}
                                        value={p[0].LookupCode}
                                    >
                                        {cacheStore.GetLocalizedValueByLookupId(p[0].LookupID)}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    {isRepresentative && (
                        <div className={styles.representativeTypeSection}>
                            <label className="bold">{getText("TypeLabel")}</label>
                            <div>
                                <select
                                    className="form-control myle-control"
                                    value={representativeType}
                                    onChange={(e: any) => setRepresentativeType(e.target.value)}
                                >
                                    {representativeTypes.map((type, idx) =>
                                        <option key={`${type[0]}_${idx}`} value={type[0]}>{type[1]}</option>
                                    )}
                                </select>
                            </div>
                        </div>
                    )}
                </div>
                {!isCreationSectionOpen && !selectedPerson && (
                    <div className={styles.searchSection}>
                        <label className="bold">{getText("SearchExistingPatientOrExternalRelationLabel")}</label>
                        <div>
                            <IndividualAdvancedSearchControl
                                cssClass="relationships"
                                resultCSS={{ maxHeight: "250px", overflowY: "auto" }}
                                defaultTextValue={searchString}
                                isFocusOnLoad={true}
                                componentId="_relationship"
                                maxResultsToReturn={4}
                                onIndividualSelectCallback={(person, searchText) => {
                                    selectPerson(person, searchText);
                                }}
                                isRenderTextbox={true}
                                individualsToOmitInSearchResults={props.existingRelationsContactIds}
                                dataCy="addRelation-popup-search"
                            />
                        </div>
                        <div className={`flex-row ${styles.createLink}`}>
                            <p>{getText("Relations_AddExternalPatientAsNewRelationLabel")}</p>
                            <a
                                className={styles.createExternalRelationButton}
                                onClick={() => setIsCreationSectionOpen(true)}
                                data-cy="addRelation-popup-createRelative"
                            >
                                {getText("Relations_CreateExternalRelationLabel")}
                            </a>
                        </div>
                    </div>
                )}

                {(!isCreationSectionOpen && selectedPerson) && (
                    <div className={`${styles.selectedPersonSection} flex-container pa-selected-items-list`}>
                        <IndividualAdvancedSearchResult
                            person={selectedPerson}
                            rootClassName="flex-container"
                            onRemove={() => clearPersonSelection()}
                        />
                    </div>
                )}

                {isCreationSectionOpen && (
                    <div className={styles.creationSection}>
                        <div className={styles.creationSectionTitle}>
                            <strong>{getText("Relations_CreateNewExternalRelationLabel")}</strong>
                            <a
                                className={styles.backToSearchButton}
                                onClick={() => setIsCreationSectionOpen(false)}
                            >
                                {getText("BackToSearchLabel")}
                            </a>
                        </div>
                        <CreateRelative
                            onChange={(relative: Relative, isFormValid: boolean) => {
                                setNewRelative(relative);
                                setIsCreationFormValid(isFormValid);
                            }}
                            onDuplicateSelect={(person: Person) => onDuplicatedSelect(person)}
                        />
                    </div>
                )}
            </div>
        </Modal>
    );
}

export { AddRelationPopup };
